/**
 * React Query hooks for Reddit Crawler Status and History
 *
 * Provides hooks for fetching:
 * - Overall crawler status and health metrics
 * - Recent crawl history records
 */

import { useQuery } from '@tanstack/react-query';
import { AdminOperationsApi } from '../services/AdminOperationsService';

// ============================================================================
// TYPES
// ============================================================================

export interface CrawlStatusDto {
  status: string;
  activeSourcesCount: number;
  inactiveSourcesCount: number;
  lastCrawlTime: string | null;
  runningCrawlsCount: number;
  recentFailuresCount: number;
  mentionsFoundLast24Hours: number;
  averageBackfillProgress: number;
  message: string;
}

/**
 * SESSION_369: DTO for region dropdown selection
 */
export interface RegionDropdownDto {
  id: string;
  name: string;
  activeSourceCount: number;
  inactiveSourceCount: number;
}

/**
 * SESSION_369: DTO for Reddit source summary
 */
export interface RedditSourceSummaryDto {
  id: string;
  subredditName: string;
  isActive: boolean;
  lastCrawledAt: string | null;
  backfillProgress: number;
  regionId: string;
}

export interface CrawlHistoryDto {
  id: string;
  redditSourceId: string;
  subredditName: string;
  startedAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
  status: string;
  postsProcessed: number;
  mentionsFound: number;
  errorMessage: string | null;
  // Sprint 027 SESSION_358: New crawl efficiency metrics
  actualRedditPostsProcessed: number | null;
  commentsProcessed: number | null;
  newContentCount: number | null;
  skippedDuplicateCount: number | null;
}

/**
 * Sprint 027 SESSION_358: Paginated response for crawl history
 */
export interface PaginatedCrawlHistoryResult {
  items: CrawlHistoryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const crawlStatusKeys = {
  all: ['crawlStatus'] as const,
  status: () => [...crawlStatusKeys.all, 'current'] as const,
  history: (hours?: number, maxResults?: number, page?: number, pageSize?: number, subredditId?: string) =>
    [...crawlStatusKeys.all, 'history', { hours, maxResults, page, pageSize, subredditId }] as const,
  // SESSION_369: Keys for regions and sources
  regions: () => [...crawlStatusKeys.all, 'regions'] as const,
  sourcesForRegion: (regionId: string, isActive?: boolean) =>
    [...crawlStatusKeys.all, 'sources', { regionId, isActive }] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Fetches the current Reddit crawler status with health metrics.
 *
 * @param options - React Query options
 * @returns Query result with crawler status
 *
 * @example
 * const { data: status, isLoading } = useCrawlStatusQuery();
 * if (status) {
 *   console.log(`Crawler status: ${status.status}`);
 *   console.log(`Active sources: ${status.activeSourcesCount}`);
 * }
 */
export function useCrawlStatusQuery(options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: crawlStatusKeys.status(),
    queryFn: async (): Promise<CrawlStatusDto> => {
      return await AdminOperationsApi.getCrawlStatus() as CrawlStatusDto;
    },
    staleTime: 30000, // 30 seconds
    refetchInterval: options?.refetchInterval, // Optional auto-refresh
  });
}

/**
 * Fetches recent crawl history records with pagination and filtering.
 * Sprint 027 SESSION_358: Added pagination (page, pageSize) and subreddit filter.
 *
 * @param hours - Number of hours to look back (default: 24)
 * @param maxResults - Maximum number of results (default: 50) - DEPRECATED, use pageSize
 * @param page - Page number for pagination (default: 1)
 * @param pageSize - Number of items per page (default: 50)
 * @param subredditId - Optional filter by subreddit ID
 * @param options - React Query options
 * @returns Query result with paginated crawl history records
 *
 * @example
 * const { data, isLoading } = useCrawlHistoryQuery(24, 50, 1, 50);
 * if (data) {
 *   console.log(`Total: ${data.totalCount}, Page ${data.page} of ${data.totalPages}`);
 *   data.items.forEach(crawl => {
 *     console.log(`${crawl.subredditName}: ${crawl.status}`);
 *   });
 * }
 */
export function useCrawlHistoryQuery(
  hours: number = 24,
  maxResults: number = 50,
  page: number = 1,
  pageSize: number = 50,
  subredditId?: string,
  options?: { refetchInterval?: number }
) {
  return useQuery({
    queryKey: crawlStatusKeys.history(hours, maxResults, page, pageSize, subredditId),
    queryFn: async (): Promise<PaginatedCrawlHistoryResult> => {
      return await AdminOperationsApi.getCrawlHistory(hours, maxResults, page, pageSize, subredditId) as PaginatedCrawlHistoryResult;
    },
    staleTime: 30000, // 30 seconds
    refetchInterval: options?.refetchInterval, // Optional auto-refresh
  });
}

/**
 * SESSION_369: Fetches all regions for dropdown selection.
 *
 * @param options - React Query options
 * @returns Query result with list of regions
 *
 * @example
 * const { data: regions, isLoading } = useRegionsQuery();
 * if (regions) {
 *   regions.forEach(region => {
 *     console.log(`${region.name}: ${region.activeSourceCount} active sources`);
 *   });
 * }
 */
export function useRegionsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: crawlStatusKeys.regions(),
    queryFn: async (): Promise<RegionDropdownDto[]> => {
      return await AdminOperationsApi.getRegions() as RegionDropdownDto[];
    },
    staleTime: 300000, // 5 minutes - regions don't change often
    enabled: options?.enabled ?? true,
  });
}

/**
 * SESSION_369: Fetches Reddit sources for a specific region.
 *
 * @param regionId - Region ID to filter by
 * @param isActive - Optional filter by active/inactive status
 * @param options - React Query options
 * @returns Query result with list of Reddit sources
 *
 * @example
 * const { data: sources, isLoading } = useRedditSourcesQuery(regionId, true);
 * if (sources) {
 *   sources.forEach(source => {
 *     console.log(`r/${source.subredditName}: ${source.isActive ? 'active' : 'inactive'}`);
 *   });
 * }
 */
export function useRedditSourcesQuery(
  regionId: string | null,
  isActive?: boolean,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: crawlStatusKeys.sourcesForRegion(regionId ?? '', isActive),
    queryFn: async (): Promise<RedditSourceSummaryDto[]> => {
      if (!regionId) return [];
      return await AdminOperationsApi.getRedditSourcesForRegion(regionId, isActive) as RedditSourceSummaryDto[];
    },
    staleTime: 60000, // 1 minute
    enabled: (options?.enabled ?? true) && !!regionId,
  });
}
